import React, { useState, useEffect, ReactElement, useRef, useCallback, KeyboardEvent } from 'react';
import Moment from 'moment';
import { actions as projectLocationActions } from '../../../../actions/projectLocationActions';
import { actions as projectManagerActions } from '../../../../actions/projectManagerActions';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators, type Dispatch } from 'redux';
import { DataTable, DataTableColReorderEvent } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { Project, LocationOption } from '../../../../types/common';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT } from '../../../../constants/constants';
import * as types from '../../../../constants/actionTypes';
import Notification from '../../../Common/Notification';
import Modal from '../../../Common/Modal/Modal';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import Modal61 from '../../../Common/Modal/Modal61';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import { PROJECT_LOCATION_BULK_UPDATE_OPTIONS as BulkOptions } from '../../../../constants/constants';
import AddToScopeDrawer from '../../../Subscription/Admin/ProjectLocations/AddToScopeDrawer';
import ChangeProjectManagerDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeProjectManagerDrawer';
import AddBaselineDrawer from '../../../Subscription/Admin/ProjectLocations/AddBaselineDrawer';
import ChangeZeroDateDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeZeroDateDrawer/ChangeZeroDateDrawer';
import signalRService from '../../../../services/signalRService';
import CustomMultiSelect from '../../../Common/PrimeMultiselectBox/CustomMultiSelect';
import { InfoWindowBody, InfoWindowTopicTitle, InfoWindowTopicBody, InfoWindowIconDescription, InfoWindowIcon, SelectAllCheck } from './styles';
import ProjectLocations from '../../../NewProjectWizard/ContentWidgets/ProjectLocations';
import { ProjectLocation } from '../../../../types/ProjectLocation';
import { MultiSelectOption } from '../../../../types/MultiSelectOption';
import { getMultiSelectFilterValues, getColumnFilterMultiSelectValues } from '../../../../helpers/multiselect';

interface LocationsProps {
	projectData: Project;
	subscriptionId: number;
	userColumns: Array<string>;
	onColumnsChange: (newColumns: Array<string>) => Promise<void>;
}

interface SearchParams {
	projectKey: string;
	baselineIds: Array<number>;
	isActive: boolean;
	pageSize: number;
	pageNumber: number;
	sortBy: string;
	sortDir: string;
	isOnHold: boolean | null;
	multiColumnSort: Array<{ [key: string]: string }>;
	isPushedDown: boolean | null;
	locationAbbreviations: Array<string>;
	projectManagerIds: Array<number>;
	zeroDates: Array<string>;
	projectZeroDates: Array<MultiSelectOption>;
	locationName: string;
}

const bulkOptions = [
	{ id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label },
	{ id: BulkOptions.Publish.value, name: BulkOptions.Publish.label },
	{ id: BulkOptions.RemoveScope.value, name: BulkOptions.RemoveScope.label },
	{ id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
	{ id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
	{ id: BulkOptions.Unpublish.value, name: BulkOptions.Unpublish.label, icon: BulkOptions.Unpublish.icon },
	{ id: BulkOptions.OnHold.value, name: BulkOptions.OnHold.label, icon: BulkOptions.OnHold.icon },
	{ id: BulkOptions.OffHold.value, name: BulkOptions.OffHold.label, icon: BulkOptions.OffHold.icon },
];

interface ProjectLocationFilters {
	projectZeroDates: Array<MultiSelectOption>;
	baselines: Array<MultiSelectOption>;
	selectedBaselines: Array<MultiSelectOption>;
	projectManagers: Array<MultiSelectOption>;
	selectedProjectManagers: Array<MultiSelectOption>;
	locationAbbreviations: string;
	locationName: string;
	selectedStatus: number;
}

const Locations = ({ projectData, subscriptionId, userColumns, onColumnsChange }: LocationsProps): ReactElement => {
	const dispatch: Dispatch = useDispatch();
	const [locations, setLocations] = useState<Array<ProjectLocation>>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedLocations, setSelectedLocations] = useState<Array<ProjectLocation>>([]);
	const [zeroDate, setZeroDate] = useState<Date>();
	const [selectedBaselines, setSelectedBaselines] = useState([]);
	const [projectManagers, setProjectManagers] = useState([]);
	const [projectBaselines, setProjectBaselines] = useState([]);
	const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
	const [applicableProjectDays, setApplicableProjectDays] = useState([0, 1, 2, 3, 4, 5, 6]);
	const [selectedBulkOperation, setSelectedBulkOperation] = useState(BulkOptions.BulkUpdate.value);
	const [isAddToScopeDrawerOpen, setIsAddToScopeDrawerOpen] = useState(false);
	const [isChangeProjectManagerDrawerOpen, setIsChangeProjectManagerDrawerOpen] = useState(false);
	const [isAddBaselineDrawerOpen, setIsAddBaselineDrawerOpen] = useState(false);
	const [isChangeZeroDateDrawerOpen, setIsChangeZeroDateDrawerOpen] = useState(false);
	const [isLoadingFormFilters, setIsLoadingFormFilters] = useState(false);
	const [showRemoveScopeWarning, setShowRemoveScopeWarning] = useState(false);
	const [showRevokePushdownWarning, setShowRevokePushdownWarning] = useState(false);
	const [showOnholdWarning, setShowOnholdWarning] = useState(false);
	const [projectManagersForDrawer, setpProjectManagersForDrawer] = useState([]);
	const [availableBulkOperations, setAvailableBulkOperations] = useState(bulkOptions);
	const [isHoldOperation, setIsHoldOperation] = useState(true);
	const [availLocations, setAvailLocations] = useState<Array<LocationOption>>([]);
	const [selectedLocationsModal, setSelectedLocationsModal] = useState<Array<LocationOption>>([]);
	const [isPublishing, setIsPublishing] = useState<Set<number>>(new Set());

	const [projectLocationListFilters, setProjectLocationListFilters] = useState<ProjectLocationFilters>({
		projectZeroDates: [],
		baselines: [],
		selectedBaselines: [],
		projectManagers: [],
		selectedProjectManagers: [],
		locationAbbreviations: '',
		locationName: '',
		selectedStatus: 0,
	});

	const [zeroDateFilters, setZeroDateFilters] = useState<Array<any>>([]);

	const [selectedProjectOwner, setSelectedProjectOwner] = useState(projectData.projectManagerId);

	const [searchParams, setSearchParams] = useState<SearchParams>({
		projectKey: projectData.projectKey,
		baselineIds: [],
		locationName: '',
		isActive: true,
		pageSize: DEFAULT_PAGE_SIZE,
		pageNumber: 1,
		sortBy: 'locationName',
		sortDir: 'asc',
		isOnHold: null,
		isPushedDown: null,
		projectZeroDates: [],
		locationAbbreviations: [],
		projectManagerIds: [],
		zeroDates: [],
		multiColumnSort: [
			{
				sortBy: 'locationName',
				sortDir: 'asc',
			},
		],
	});

	const [totalCount, setTotalCount] = useState<number>(0);
	const [columns, setColumns] = useState<Array<any>>([]);

	const statusOptions = [
		{ label: 'All', value: 0 },
		{ label: 'Published', value: 1 },
		{ label: 'Draft', value: 2 },
	];

	const getLocations = async (params) => dispatch(projectLocationActions.searchProjectLocations(params));
	const addLocationToScope = async (params) => dispatch(projectLocationActions.addToScope(params));
	const loadProjectLocationListFilters = async (projectKey) => dispatch(projectLocationActions.loadProjectLocationListFilters(projectKey));
	const getProjectManagers = async (params) => dispatch(projectManagerActions.searchProjectManagers(params));
	const removeFromScope = async (params) => dispatch(projectLocationActions.removeFromScope(params));
	const getBaselines = async (subscriptionId, projectKey) => dispatch(projectLocationActions.loadPushdownFilters(subscriptionId, projectKey));
	const postPushdown = async (params) => dispatch(projectLocationActions.pushdown(params));
	const revokePushdown = async (locationIds: Array<number>, projectKey: string) =>
		dispatch(projectLocationActions.revokePushdown(locationIds, projectKey));
	const postOnHold = async (locationIds: Array<number>, projectKey: string, isOnHold: boolean) =>
		dispatch(projectLocationActions.updateOnHold(locationIds, projectKey, isOnHold));

	const publishModal = useRef();
	const infoWindow = useRef();
	const addProjectLocationsModal = useRef();

	const formatBaselines = async () => {
		const originalData = await getBaselines(subscriptionId, projectData.projectKey);

		const transformedData = originalData.payload.baselines.map((item) => ({
			name: item.name,
			code: item.id,
			id: item.id,
		}));

		return transformedData;
	};

	const formatProjectManagers = async () => {
		const originalData = await getProjectManagers({
			IsActive: true,
			PageSize: 0,
			SortBy: 'name',
		});

		setpProjectManagersForDrawer(originalData.payload.results);

		const transformedData = originalData.payload.results.map((item) => ({
			label: item.name,
			value: item.id,
		}));

		return transformedData;
	};

	const formatLoadProjectLocationListFilters = async () => {
		setIsLoadingFormFilters(true);
		const originalData = await loadProjectLocationListFilters(projectData.projectKey);
		const payload = originalData.payload;

		if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_SUCCESS) {
			setProjectLocationListFilters({ ...projectLocationListFilters, ...payload });
			setZeroDateFilters(payload.projectZeroDates);
			setIsLoadingFormFilters(false);
		} else if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_FAILURE) {
			Notification('error', 'An Error Occurred', payload.message);
			setIsLoadingFormFilters(false);
		}
	};

	const toggleAdvancedOptions = () => {
		setShowAdvancedOptions(!showAdvancedOptions);
	};

	const search = async (params: SearchParams) => {
		setIsLoading(true);
		try {
			const result = await getLocations(params);
			const payload = result.payload;
			if (result.type === types.SEARCH_PROJECTLOCATION_SUCCESS) {
				setSelectedBaselines([]);
				setSelectedLocations([]);
				setLocations(result.payload.results);
				setTotalCount(result.payload.totalCount);

				const currentProjectLocations = result.payload.results.map((item) => ({
					abbreviation: item.locationAbbreviation,
					id: item.locationId,
					name: item.locationName,
				}));

				setSelectedLocationsModal(currentProjectLocations);
			} else if (result.type === types.SEARCH_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const removeFromScopeClick = async (row) => {
		try {
			const param = {
				projectLocationIds: [row.id],
				projectKey: projectData.projectKey,
			};
			const result = await removeFromScope(param);
			const payload = result.payload;

			if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
				Notification('success', '', 'The location was removed from scope');
				search(searchParams);
			} else if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const addLocationsToScope = async () => {
		const selectedLocationIds: number[] = selectedLocationsModal.map((item) => item.id);

		try {
			const params = {
				projectKey: projectData.projectKey,
				subscriptionId: subscriptionId,
				locationIds: selectedLocationIds,
			};
			const response = await addLocationToScope(params);
			if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_SUCCESS) {
				Notification('success', '', `Locations added succesfully`);
				search(searchParams);
				addProjectLocationsModal.current.hide();
			} else if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const openPublishModal = async (row) => {
		setSelectedLocations([row]);
		publishModal.current.show();
	};

	const onRevokePushdownIcon = async (row) => {
		try {
			setIsLoading(true);
			const response = await revokePushdown(
				[row].map((m) => m.id),
				projectData.projectKey
			);
			if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowRevokePushdownWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Revoke Pushdown was successful');
					search(searchParams);
				}
			} else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsLoading(false);
		}
	};

	const closeInfoWindow = async () => {
		infoWindow.current.hide();
	};

	const UpdateOnHoldClick = async (row) => {
		try {
			setIsLoading(true);

			const result = await postOnHold([row.id], projectData.projectKey, !row.isOnHold);
			const payload = result.payload;

			if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
				const notificationMessage = row.isOnHold ? 'activated' : 'on-hold';

				Notification('success', '', `The Project Location was successfully ${notificationMessage}`);
				search(searchParams);
				publishModal.current.hide();
			} else if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onViewChange = async (e: string) => {
		setSelectedBulkOperation(e);
	};

	const onRemoveFromScope = async () => {
		try {
			setIsLoading(true);
			const request = {
				subscriptionId: subscriptionId,
				projectKey: projectData.projectKey,
				projectLocationIds: selectedLocations.map((x) => x.id),
			};
			const response = await removeFromScope(request);
			if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
				if (response.payload.skippedPushedLocations) {
					setShowRemoveScopeWarning(true);
				}
				if (response.payload.removedSomeLocations) {
					Notification('success', 'Success', 'Remove from Scope completed successfully');
					search(searchParams);
				}
			} else if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onChangeManagerIcon = async (row) => {
		setSelectedLocations([row]);
		setIsChangeProjectManagerDrawerOpen(true);
	};

	const onChangeZeroDateIcon = (row) => {
		setSelectedLocations([row]);
		setIsChangeZeroDateDrawerOpen(true);
	};

	const onsAddBaselineDrawerIcon = (row) => {
		setSelectedLocations([row]);
		setIsAddBaselineDrawerOpen(true);
	};

	const onChangeManager = async () => {
		setIsChangeProjectManagerDrawerOpen(true);
	};

	const onChangeZeroDate = () => {
		setIsChangeZeroDateDrawerOpen(true);
	};

	const onRevokePushdown = async () => {
		try {
			setIsLoading(true);
			const response = await revokePushdown(
				selectedLocations.map((m) => m.id),
				projectData.projectKey
			);
			if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowRevokePushdownWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Revoke Pushdown was successful');
					search(searchParams);
				}
			} else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsLoading(false);
		}
	};

	const offHold = () => {
		updateOnHold(false);
	};

	const onHold = () => {
		updateOnHold(true);
	};

	const updateOnHold = async (isOnHold: boolean) => {
		setIsHoldOperation(isOnHold);
		try {
			const response = await postOnHold(
				selectedLocations.map((m) => m.id),
				projectData.projectKey,
				isOnHold
			);
			if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowOnholdWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Locations updated successfully');
					search(searchParams);
				}
			} else if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	const selectBulkOperation = async () => {
		switch (selectedBulkOperation) {
			case BulkOptions.BulkUpdate.value:
				break;
			case BulkOptions.AddBaseline.value:
				setIsAddBaselineDrawerOpen(true);
				break;
			case BulkOptions.Publish.value:
				setSelectedBaselines([]);
				publishModal.current.show();
				break;
			case BulkOptions.RemoveScope.value:
				onRemoveFromScope();
				break;
			case BulkOptions.ChangeManager.value:
				onChangeManager();
				break;
			case BulkOptions.ChangeZeroDate.value:
				onChangeZeroDate();
				break;
			case BulkOptions.Unpublish.value:
				onRevokePushdown();
				break;
			case BulkOptions.OnHold.value:
				onHold();
				break;
			case BulkOptions.OffHold.value:
				offHold();
				break;
			default:
				break;
		}
	};

	const headerComponent = () => {
		return (
			<div className='header-buttons'>
				<SelectAllCheck>
					<Checkbox
						onChange={toggleSelectAll} // Handle the "Select All" checkbox change
						checked={selectedLocations.length === locations.length}
					/>
					<span>Select All</span>
				</SelectAllCheck>
				<MultiSelectBox
					name='views'
					idField='id'
					valueField='name'
					placeholder='Select Bulk Action'
					className={'multi-select-bulk'}
					options={availableBulkOperations}
					onChange={onViewChange}
					selectedValues={selectedBulkOperation}
					isMulti={false}
					isSearchable={false}
				/>
				<Button label='Apply' onClick={selectBulkOperation} disabled={selectedLocations.length === 0} className='pacer-table-button' />
			</div>
		);
	};

	// Function to handle "Select All" checkbox click
	const toggleSelectAll = () => {
		if (selectedLocations.length === locations.length) {
			// All rows are selected, so clear selection
			setSelectedLocations([]);
		} else {
			// Not all rows are selected, so select all
			setSelectedLocations([...locations]);
		}
	};

	const checkboxTemplate = useCallback(
		(rowData) => {
			return (
				<Checkbox
					name='checkbox'
					checked={selectedLocations.some((location) => location.id === rowData.id)}
					onChange={() => toggleRowSelection(rowData)}
				/>
			);
		},
		[selectedLocations]
	);

	const statusBodyTemplate = (rowData) => {
		let description = 'Draft';
		let color = 'icon-orange';

		if (rowData.isPushedDown === true && rowData.isOnHold === false) {
			description = 'Published';
			color = 'icon-green';
		}

		if (rowData.isOnHold === true) {
			description = 'On Hold';
			color = 'icon-black';
		}

		return (
			<div className='pacer-badge-container'>
				<span className='pacer-badge'>
					<i className={`pi pi-circle-on ${color}`}></i>
					<span className='pacer-status'>{description}</span>
				</span>
			</div>
		);
	};

	const zeroDateBodyTemplate = (rowData) => {
		let value = '-';

		if (rowData.zeroDate) {
			value = Moment.utc(rowData.zeroDate).format(DATE_FORMAT);
		}

		return <span>{value}</span>;
	};

	const baselineBodyTemplate = (rowData: ProjectLocation) => {
		if (rowData.baselines.length === 0) {
			return <span>-</span>;
		}

		return rowData.baselines.map((item) => <div key={item.id}>{item.name}</div>);
	};

	const actionBodyTemplate = (rowData) => {
		if (rowData.isPushedDown === true && rowData.isOnHold === false) {
			return (
				<>
					<Button
						icon='fa-regular fa-calendar-lines-pen'
						onClick={() => onChangeZeroDateIcon(rowData)}
						title='Change Zero Date'
						className='fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-square-list'
						onClick={() => onsAddBaselineDrawerIcon(rowData)}
						title='Add Baselines'
						className='fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-user-pen'
						onClick={() => onChangeManagerIcon(rowData)}
						title='Change Project Manager'
						className='fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-circle-pause'
						onClick={() => UpdateOnHoldClick(rowData)}
						title='Pause Project'
						className='fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-arrows-rotate'
						onClick={() => onRevokePushdownIcon(rowData)}
						title='Revoke Project'
						className='fa-lg mx-2'
					/>
				</>
			);
		}

		if (rowData.isPushedDown === true && rowData.isOnHold === true) {
			return (
				<>
					<Button
						icon='fa-regular fa-circle-play'
						onClick={() => UpdateOnHoldClick(rowData)}
						title='Unpause Project'
						className='fa-lg mx-2'
					/>
				</>
			);
		}

		return (
			<>
				<Button icon='fa-regular fa-paper-plane-top' onClick={() => openPublishModal(rowData)} title='Publish' className='fa-lg mx-2' />
				<Button
					icon='fa-regular fa-trash-can'
					onClick={() => removeFromScopeClick(rowData)}
					title='Remove from Scope'
					className='fa-lg mx-2'
					style={{ color: '#ef4444' }}
				/>
			</>
		);
	};

	const onPageChange = async (e) => {
		const updatedParams = {
			...searchParams,
			pageNumber: e.page + 1,
			pageSize: e.rows,
		};

		setSearchParams(updatedParams);
	};

	// Function to handle individual row checkbox click
	const toggleRowSelection = (row) => {
		if (selectedLocations.some((location) => location.id === row.id)) {
			// Remove the row based on its id
			setSelectedLocations(selectedLocations.filter((location) => location.id !== row.id));
		} else {
			// Add the row to selected locations
			setSelectedLocations([...selectedLocations, row]);
		}
	};

	const onMultiSelectChange = (filterName, searchName, e) => {
		// Create a copy of the current filters & searchParams (new object always triggers effect)
		const updatedFilterParams = { ...projectLocationListFilters };
		const updatedSearchParams = { ...searchParams };

		// Update the copies with the new property name and value
		updatedSearchParams[searchName] = getMultiSelectFilterValues(e);
		updatedFilterParams[filterName] = getColumnFilterMultiSelectValues(updatedSearchParams[searchName], projectLocationListFilters[filterName]);

		setProjectLocationListFilters(updatedFilterParams);
		setSearchParams(updatedSearchParams);
	};

	const onStatusChange = (value) => {
		const updatedFilterParams = { ...projectLocationListFilters };
		updatedFilterParams.selectedStatus = value;
		setProjectLocationListFilters(updatedFilterParams);

		const updatedSearchParams = { ...searchParams };
		updatedSearchParams.isPushedDown = value == 0 ? null : value == 1 ? true : false;
		setSearchParams(updatedSearchParams);
	};

	const buildSearchRequest = (): SearchParams => {
		return {
			...searchParams,
			locationName: projectLocationListFilters.locationName,
			locationAbbreviations: abbreviationsToArray(projectLocationListFilters.locationAbbreviations),
		};
	};

	const abbreviationsToArray = (abbreviations) => {
		if (!abbreviations) {
			return [];
		}

		if (abbreviations.includes(',')) {
			return abbreviations.split(',');
		}

		return abbreviations.split(' ');
	};

	const filterChange = (e: { target: { name: string; value: string } }) => {
		const name = e.target.name;
		const updatedParams = { ...projectLocationListFilters };
		updatedParams[name] = e.target.value;
		setProjectLocationListFilters(updatedParams);

		setSearchParams(buildSearchRequest());
	};

	const keyUpFilter = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			const target = event.target as HTMLInputElement;
			const name = target.name;
			const updatedParams = { ...projectLocationListFilters };
			updatedParams[name] = target.value;
			setProjectLocationListFilters(updatedParams);

			setSearchParams(buildSearchRequest());
		}
	};

	const onAddLocations = () => {
		addLocationsToScope();
	};

	const onPublishModal = async () => {
		const locationIds = selectedLocations.map((location) => location.locationId).filter((id): id is number => id !== undefined);

		try {
			setIsLoading(true);

			setIsPublishing((prev) => new Set([...prev, ...locationIds]));

			const param = {
				projectManagerId: selectedProjectOwner,
				projectKey: projectData.projectKey,
				zeroDate: Moment.utc(zeroDate).format(DATE_FORMAT),
				BaselineIds: selectedBaselines.map((obj) => obj.code),
				LocationIds: selectedLocations.map((obj) => obj.locationId),
				sendEmail: true,
				daysSelected: applicableProjectDays,
			};

			const result = await postPushdown(param);
			const payload = result.payload;

			if (result.type === types.PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				publishModal.current.hide();
				Notification(
					'info',
					'Publishing Project',
					'We are publishing your project. This may take several minutes. You can move on to your next tasks in the meantime and we will email you when the project has published to all teams.'
				);
			} else if (result.type === types.PUSHDOWN_PROJECTLOCATION_FAILURE) {
				setIsPublishing((prev) => {
					const updated = new Set(prev);
					locationIds.forEach((id) => updated.delete(id));
					return updated;
				});

				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			// Remove the locations from isPublishing
			setIsPublishing((prev) => {
				const updated = new Set(prev);
				locationIds.forEach((id) => updated.delete(id));
				return updated;
			});
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onAddScopeCancel = () => {
		setIsAddToScopeDrawerOpen(false);
	};

	const onAddScopeComplete = () => {
		setIsAddToScopeDrawerOpen(false);
		search(searchParams);
	};

	const onChangeManagerComplete = () => {
		setIsChangeProjectManagerDrawerOpen(false);
		search(searchParams);
	};

	const onChangeManagerCancel = () => {
		setIsChangeProjectManagerDrawerOpen(false);
	};

	const onAddBaselineCancel = () => {
		setIsAddBaselineDrawerOpen(false);
	};

	const onAddBaselineComplete = () => {
		setIsAddBaselineDrawerOpen(false);
		search(searchParams);
	};

	const onChangeZeroDateComplete = () => {
		setIsChangeZeroDateDrawerOpen(false);
		search(searchParams);
	};

	const onChangeZeroDateCancel = () => {
		setIsChangeZeroDateDrawerOpen(false);
	};

	const onColumnReorder = async (e: DataTableColReorderEvent) => {
		// Save columns personalization
		const newColumns: Array<string | undefined> = e.columns.map((c) => c.props.field);
		await onColumnsChange(newColumns.filter((x) => x != null));
	};

	const closeRemoveScopeWarning = () => {
		setShowRemoveScopeWarning(false);
	};

	const closeRevokePushdownWarning = () => {
		setShowRevokePushdownWarning(false);
	};

	const closeOnholdWarning = () => {
		setShowOnholdWarning(false);
	};

	const getAdditionalBaselines = (): Array<MultiSelectOption> => {
		//returning an array of Baselines that are not already pushed down to any of the selected locations
		//comparison is done by Baseline name: app does not allow duplicate baseline names in the same subscription
		const baselineNames = new Set(selectedLocations.flatMap((location) => location.baselines).map((baseline) => baseline.name));

		return projectBaselines.filter(({ name }) => !baselineNames.has(name));
	};

	const scopeWarningModalBody =
		'Some locations were not removed from scope because the project has been published to them. Before removing locations from scope, consider filtering the Status column to a value of Published.';
	const scopeWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRemoveScopeWarning}>
				Ok
			</button>
		</div>
	);

	const revokePushdownWarningModalBody =
		'Some locations/teams were not updated because the project has not been published to them. Before unpublishing, consider filtering the Status column to a value of Published.';
	const revokePushdownWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRevokePushdownWarning}>
				Ok
			</button>
		</div>
	);

	const onholdWarningModalBody = `Some locations/teams were not updated because the project has not been published to them. Before selecting ${
		isHoldOperation ? 'On Hold' : 'Off Hold'
	}, consider filtering the Status column to a value of Published.`;

	const onholdWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeOnholdWarning}>
				Ok
			</button>
		</div>
	);

	const buildColumns = () => {
		const cols = [
			{
				name: 'locationName',
				column: (
					<Column
						key='locationName'
						field='locationName'
						header='Name'
						filter={true}
						sortable={true}
						style={{ width: '300px' }}
						filterElement={
							<input
								type='text'
								name='locationName'
								className='p-inputtext'
								placeholder='Filter by Name'
								onBlur={filterChange}
								onKeyUp={keyUpFilter}
								defaultValue={projectLocationListFilters.locationName}
							/>
						}
					/>
				),
			},
			{
				name: 'locationAbbreviation',
				column: (
					<Column
						key='locationAbbreviation'
						field='locationAbbreviation'
						header='Abbreviation'
						sortable={true}
						filter={true}
						style={{ width: '100px' }}
						filterElement={
							<input
								type='text'
								name='locationAbbreviations'
								className='p-inputtext'
								placeholder='Filter by Abbreviation'
								onBlur={filterChange}
								onKeyUp={keyUpFilter}
								defaultValue={projectLocationListFilters.locationAbbreviations}
							/>
						}
					/>
				),
			},
			{
				name: 'status',
				column: (
					<Column
						key='status'
						field='status'
						body={statusBodyTemplate}
						header='Status'
						style={{ width: '110px' }}
						sortable={true}
						filter={true}
						filterElement={
							<Dropdown
								name='isPushedDown'
								className='ui-column-filter w-100'
								value={projectLocationListFilters.selectedStatus}
								options={statusOptions}
								optionLabel='label'
								optionValue='value'
								onChange={(e) => onStatusChange(e.value)}
							/>
						}
					/>
				),
			},
			{
				name: 'zeroDates',
				column: (
					<Column
						key='zeroDates'
						body={zeroDateBodyTemplate}
						field='zeroDates'
						header='Zero Date'
						style={{ width: '100px' }}
						filter={true}
						sortable={true}
						filterElement={
							<CustomMultiSelect
								className='ui-column-filter'
								optionLabel='name'
								dataKey='id'
								placeholder='Select'
								value={searchParams.projectZeroDates}
								options={zeroDateFilters}
								onChange={(e) => onMultiSelectChange('projectZeroDates', 'projectZeroDates', e)}
								onButtonClick={() => {
									/*Do nothing*/
								}}
							/>
						}
					/>
				),
			},
			{
				name: 'baselines',
				column: (
					<Column
						key='baselines'
						body={baselineBodyTemplate}
						field='baselines'
						header='Baselines Assigned'
						filter={true}
						filterElement={
							<CustomMultiSelect
								className='ui-column-filter'
								dataKey='id'
								optionLabel='name'
								placeholder='Select'
								value={projectLocationListFilters.selectedBaselines}
								options={projectLocationListFilters.baselines}
								onChange={(e) => onMultiSelectChange('selectedBaselines', 'baselineIds', e)}
								onButtonClick={() => {
									/*Do nothing*/
								}}
							/>
						}
					/>
				),
			},
			{
				name: 'projectManager',
				column: (
					<Column
						key='projectManager'
						field='projectManager'
						header='Implementation Manager'
						filter={true}
						filterElement={
							<CustomMultiSelect
								className='ui-column-filter'
								dataKey='id'
								optionLabel='name'
								placeholder='Select'
								value={projectLocationListFilters.selectedProjectManagers}
								options={projectLocationListFilters.projectManagers}
								onChange={(e) => onMultiSelectChange('selectedProjectManagers', 'projectManagerIds', e)}
								onButtonClick={() => {
									/*Do nothing*/
								}}
							/>
						}
					/>
				),
			},
		];

		let selectedColumns: Array<React.JSX.Element> = [];

		// Look for columns personalization, else use the default
		if (userColumns && userColumns.length) {
			for (const uc of userColumns) {
				const col = cols.find((c) => c.name === uc);
				if (col) {
					selectedColumns.push(col.column);
				}
			}
		} else {
			selectedColumns = cols.map((x) => x.column);
		}

		selectedColumns.unshift(
			<Column key='isChecked' field='isChecked' body={checkboxTemplate} style={{ textAlign: 'center', width: '40px' }} reorderable={false} />
		);
		selectedColumns.push(
			<Column key='actions' field='actions' body={actionBodyTemplate} style={{ width: '260px', textAlign: 'right' }} reorderable={false} />
		);

		setColumns(selectedColumns);
	};

	useEffect(() => {
		setColumns((prevColumns) => {
			if (prevColumns.some((col) => col.key === 'isChecked')) {
				return prevColumns.map((col) =>
					col.key === 'isChecked' ? (
						<Column
							key='isChecked'
							field='isChecked'
							body={checkboxTemplate}
							style={{ textAlign: 'center', width: '40px' }}
							reorderable={false}
						/>
					) : (
						col
					)
				);
			}
			return [
				<Column
					key='isChecked'
					field='isChecked'
					body={checkboxTemplate}
					style={{ textAlign: 'center', width: '40px' }}
					reorderable={false}
				/>,
				...prevColumns,
			];
		});
	}, [selectedLocations, checkboxTemplate]);

	useEffect(() => {
		const params = buildSearchRequest();
		search(params);
	}, [searchParams]);

	useEffect(() => {
		const specificOptionsToToggle = [
			{ id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label },
			{ id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
			{ id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
		];

		const draftCount = selectedLocations.filter((row) => row.isPushedDown == false).length;

		if (draftCount === 0) {
			// Add specific options if not already present
			setAvailableBulkOperations((prevOptions) => {
				const newOptions = specificOptionsToToggle.filter((option) => !prevOptions.some((existingOption) => existingOption.id === option.id));
				return [...prevOptions, ...newOptions];
			});
		} else {
			// Remove specific options
			setAvailableBulkOperations((prevOptions) =>
				prevOptions.filter((option) => !specificOptionsToToggle.some((specificOption) => specificOption.id === option.id))
			);
		}
	}, [selectedLocations]);

	useEffect(() => {
		const fetchData = async () => {
			const [baselines, managers] = await Promise.all([formatBaselines(), formatProjectManagers()]);

			setProjectBaselines(baselines);
			setProjectManagers(managers);

			await formatLoadProjectLocationListFilters();

			setSelectedBulkOperation(BulkOptions.Publish.value);
		};

		fetchData();

		// Join a group based on subscription
		signalRService.joinGroup(projectData.projectKey);

		// Initialize the SignalR service and set up global event listeners
		signalRService.on('UpdateProjectLocationStatus', (projectLocation) => {
			setIsPublishing((prev) => {
				const updated = new Set(prev);
				updated.delete(projectLocation.locationId);
				return updated;
			});

			// Update the specific record in the locations state
			setLocations((prevLocations) => {
				// Find the index of the location with the matching ID
				const locationIndex = prevLocations.findIndex((loc) => loc.id === projectLocation.id);

				if (locationIndex !== -1) {
					// Replace the existing record with the new one
					const updatedLocations = [...prevLocations];
					updatedLocations[locationIndex] = projectLocation;
					return updatedLocations;
				}

				// If no matching record is found, return the existing locations
				return prevLocations;
			});
		});
	}, []);

	useEffect(() => {
		if (
			projectLocationListFilters &&
			projectLocationListFilters.baselines?.length > 0 &&
			projectLocationListFilters.projectManagers?.length > 0 &&
			zeroDateFilters?.length > 0
		) {
			buildColumns();
		}
	}, [projectLocationListFilters, zeroDateFilters]);

	const renderColumns = () => {
		// I have no idea why this is here... this looks superfluous
		const cols = columns.map((col) => {
			return col;
		});

		return cols;
	};

	return (
		<div className='publish-content'>
			<div className='d-flex justify-content-end'>
				<button
					type='button'
					className='btn pacer61-primary-purple-btn my-3'
					onClick={() => {
						addProjectLocationsModal.current.show();
					}}>
					<i className='fa-regular fa-circle-plus'></i>
					<span className='mx-2'>Add Location/Team to Scope</span>
				</button>
			</div>

			<DataTable
				filterDisplay='row'
				value={locations}
				header={headerComponent()}
				className='p-datatable-sm p-datatable-striped'
				loading={isLoading}
				sortField='locationName'
				sortOrder={1}
				reorderableColumns
				onColReorder={(e) => onColumnReorder(e)}>
				{renderColumns()}
			</DataTable>

			<Paginator
				leftContent={<span>Rows per page:</span>}
				rows={searchParams.pageSize}
				totalRecords={totalCount}
				first={(searchParams.pageNumber - 1) * searchParams.pageSize}
				rowsPerPageOptions={[20, 50, 100]}
				onPageChange={onPageChange}
				template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
				currentPageReportTemplate='{first}-{last} of {totalRecords}'
			/>

			{isAddToScopeDrawerOpen && (
				<AddToScopeDrawer
					subscriptionId={subscriptionId}
					projectKey={projectData.projectKey}
					onCancel={onAddScopeCancel}
					onComplete={onAddScopeComplete}
					projectLocationActions={projectLocationActions}
				/>
			)}
			{isChangeProjectManagerDrawerOpen && (
				<ChangeProjectManagerDrawer
					projectManagers={projectManagersForDrawer}
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onChangeManagerCancel}
					onComplete={onChangeManagerComplete}
					isLoading={isLoadingFormFilters}
				/>
			)}
			{isAddBaselineDrawerOpen && (
				<AddBaselineDrawer
					baselines={getAdditionalBaselines()}
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onAddBaselineCancel}
					onComplete={onAddBaselineComplete}
					isLoading={isLoadingFormFilters}
				/>
			)}
			{isChangeZeroDateDrawerOpen && (
				<ChangeZeroDateDrawer
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onChangeZeroDateCancel}
					onComplete={onChangeZeroDateComplete}
				/>
			)}
			{showRemoveScopeWarning && (
				<Modal
					id='pushedDown'
					title='Skipped Locations'
					body={scopeWarningModalBody}
					onCloseClick={closeRemoveScopeWarning}
					customFooter={scopeWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}
			{showRevokePushdownWarning && (
				<Modal
					id='revokePushdownWarning'
					title='Skipped Locations'
					body={revokePushdownWarningModalBody}
					onCloseClick={closeRevokePushdownWarning}
					customFooter={revokePushdownWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}
			{showOnholdWarning && (
				<Modal
					id='onholdWarning'
					title='Skipped Locations'
					body={onholdWarningModalBody}
					onCloseClick={closeOnholdWarning}
					customFooter={onholdWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}

			<Modal61
				ref={infoWindow}
				body={
					<InfoWindowBody>
						<div className='row justify-content-center'>
							<InfoWindowIcon>
								<i className='fa-regular fa-paper-plane'></i>
							</InfoWindowIcon>
						</div>
						<div className='row justify-content-center'>
							<InfoWindowIconDescription>
								Almost Complete!
								<br />
								Ready to Publish?
							</InfoWindowIconDescription>
						</div>

						<InfoWindowTopicTitle>What is a Project Zero Date?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							In order to publish a project to a location, you will need to set a Project Zero Date. This is the ‘go live’ or ‘launch
							date’ of a selected location.
						</InfoWindowTopicBody>

						<InfoWindowTopicTitle>What does publishing a project mean?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							Publishing a project will allow the selected location(s) to see this project within their Dashboard. Prior to publication,
							the project will live in a draft state. You can always make changes post publication.
						</InfoWindowTopicBody>
					</InfoWindowBody>
				}
				customFooter={
					<>
						<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeInfoWindow}>
							Go to Publish Page
						</button>
					</>
				}
				title={''}
				primaryButtonText={''}
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={closeInfoWindow}
				isLoading={false}
				isCentered={true}
			/>

			<Modal61
				ref={publishModal}
				title='Publish Project'
				body={
					<>
						<div className='row'>
							<div className='col-md-6'>
								<div>
									<label className='title'>Baseline(s):</label>
								</div>
								<MultiSelect
									optionLabel='name'
									required={true}
									className='w-100'
									value={selectedBaselines}
									options={projectBaselines}
									display='chip'
									onChange={(e) => {
										setSelectedBaselines(e.value);
									}}
								/>
							</div>
							<div className='col-md-6'>
								<div>
									<label className='title'>Zero Date:</label>
								</div>
								<Calendar
									value={zeroDate}
									className='pacer-calendar'
									onChange={(e) => setZeroDate(e.value)}
									placeholder='When is this project complete?'
									dateFormat='dd M yy'
									showIcon
									iconPos='left'
								/>
							</div>
						</div>
						<div className='row'>
							<div className='col-12 text-end'>
								<button className='btn btn-link mt-3 mb-3' onClick={toggleAdvancedOptions}>
									{showAdvancedOptions ? (
										<>
											<i className='pi pi-minus'></i> Hide Advanced Options
										</>
									) : (
										<>
											<i className='pi pi-plus'></i> Show Advanced Options
										</>
									)}
								</button>
							</div>
						</div>

						{showAdvancedOptions && (
							<div className='row'>
								<div className='col-md-6'>
									<div>
										<label className='title'>Project Owner:</label>
									</div>
									<Dropdown
										value={selectedProjectOwner}
										options={projectManagers}
										className='w-100'
										onChange={(e) => setSelectedProjectOwner(e.value)}
										placeholder='Select Project Owner'
									/>
								</div>
							</div>
						)}
					</>
				}
				customFooter={
					<>
						<button
							type='button'
							className='btn btn-primary'
							data-bs-dismiss='modal'
							onClick={onPublishModal}
							disabled={selectedLocations.some((loc) => isPublishing.has(loc.locationId))}>
							Publish
						</button>
					</>
				}
				primaryButtonText='Publish'
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={undefined}
				isLoading={false}
				isCentered={false}
			/>

			<Modal61
				ref={addProjectLocationsModal}
				title='Select locations'
				body={
					<ProjectLocations
						projectData={projectData}
						setIsLoading={setIsLoading}
						subscriptionId={subscriptionId}
						locationsState={{
							availableLocations: availLocations,
							selectedLocations: selectedLocationsModal,
							setAvailableLocations: setAvailLocations,
							setSelectedLocations: setSelectedLocationsModal,
						}}></ProjectLocations>
				}
				customFooter={
					<>
						<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={onAddLocations}>
							Add Locations
						</button>
					</>
				}
				primaryButtonText='Publish'
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={undefined}
				isLoading={false}
				isCentered={false}
			/>
		</div>
	);
};

const mapDispatchToProps = (dispatch) => {
	return {
		projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
	};
};

export default connect(mapDispatchToProps)(Locations);
